import _defineProperty from "@babel/runtime/helpers/defineProperty";
var _DateUtils;
import "@abinitio/kendo.all";
import { ClassUtils } from "abweb/utils/ClassUtils";
import { DateTimeUtils } from "abweb/utils/date/DateTimeUtils";

/**
 * DateUtils holds non-localized helper methods for Date and DateTime reasoning.
 * Do NOT add localized reasoning - see DateTimeUtils for localized Date and DateTime support,
 * which also include localized and non-localized formatting and parsing..
 */
export class DateUtils {
  constructor() {
    // static class, can't create an instance
  }

  ///////////  Custom formats supported by DateUtils ///////////////////

  static resetStatics() {
    // Nothing to do, as no statics (and NO localized behavior!)
  }

  /**
   * Compares two Date objects and returns a number indicating if the first Date object
   * is before, equal to or after the second item.
   * @param date1 Date object
   * @param date2 Date object
   * @return 0 if date1 are both null or if they are equal,
   * -1 if date1 is null or before date2,
   * 1 if date2 is null or before date1
   */
  static compare(date1, date2) {
    if (date1 == null && date2 == null) {
      return 0;
    }
    if (date1 == null) {
      return -1;
    }
    if (date2 == null) {
      return 1;
    }

    // at this point we know that both dates are non-null
    const delta = date1.getTime() - date2.getTime();
    if (delta === 0) {
      return 0;
    }
    return delta < 0 ? -1 : 1;
  }

  /**
   * Compares two Date objects for equality.
   * @param date1 Date object
   * @param date2 Date object
   * @return true if the two Date objects represent the same datetime.
   */
  static areEqual(date1, date2) {
    return DateUtils.compare(date1, date2) === 0;
  }

  /**
   * Clones a Date object.
   * @param date Date
   * @returns {Date} New Date object whose value equals date.
   */
  static clone(date) {
    if (date == null) {
      return null;
    }
    return new Date(date.getTime());
  }
  static toHHMMSS(value) {
    let hours = Math.floor(value / 3600);
    let minutes = Math.floor((value - hours * 3600) / 60);
    let seconds = Math.floor(value - hours * 3600 - minutes * 60);
    if (hours < 10) {
      hours = "0" + hours;
    }
    if (minutes < 10) {
      minutes = "0" + minutes;
    }
    if (seconds < 10) {
      seconds = "0" + seconds;
    }
    if (hours === "00") {
      return `${minutes}:${seconds}`;
    }
    return `${hours}:${minutes}:${seconds}`;
  }

  /**
   * Convert a date to a format that Excel will tolerate.
   * In particular, NO MILLISECONDS.
   * @param date Date
   * @returns {string} Excel format date
   */
  static toCSV(date) {
    return date ? DateTimeUtils.FORMAT_TIMESTAMP_CSV.format(date) : " ";
  }

  /**
   * Changes an incoming date from UTC-midnight to Local-midnight. This is not a method for use with datetime values,
   * but for functions that represent just a YYYY/MM/DD.
   * @param {Date} date Date object whose UTC value represents the intended YYYY/MM/DD value.
   * @returns {Date} Date object whose Local value represents the intended YYYY/MM/DD value.
   */
  static convertDateFromUtcToLocal(date) {
    return date ? new Date(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate()) : null;
  }

  /**
   * Changes an incoming date from Local-midnight to UTC-midnight. This is not a method for use with datetime values,
   * but for functions that represent just a YYYY/MM/DD.
   * @param {Date} date Date object whose Local value represents the intended YYYY/MM/DD value.
   * @returns {Date} Date object whose UTC value represents the intended YYYY/MM/DD value.
   */
  static convertDateFromLocalToUtc(date) {
    return date ? new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate())) : null;
  }
  static getMidnightFor(date) {
    return new Date(date.getFullYear(), date.getMonth(), date.getDate()); // no time part
  }

  /**
   * Returns Date at midnight UTC, such that it matches the specified day when
   * converted to local time. The time part of the specified date is ignored.
   * This is useful when working with UTC dates returned by queries.
   */
  static getMidnightUTCFor(date) {
    let day = date.getDate();
    if (date.getTimezoneOffset() > 0) {
      // Midnight UTC of next day matches day of specified date
      day++;
    }
    return new Date(Date.UTC(date.getFullYear(), date.getMonth(), day)); // no time part
  }

  /**
   * Creates a new Date object by adding the current time zone offset to the date argument.
   * @param date
   */
  static addTimeZoneOffset(date) {
    return DateUtils.offsetDateByMinutes(date, date.getTimezoneOffset());
  }
  static offsetDateByDays(date, numDays) {
    return new Date(date.getTime() + numDays * 24 * 60 * 60 * 1000);
  }
  static offsetDateByHours(date, numHours) {
    return new Date(date.getTime() + numHours * 60 * 60 * 1000);
  }
  static offsetDateByMinutes(date, numMinutes) {
    return new Date(date.getTime() + numMinutes * 60 * 1000);
  }

  ///////   Start of Luxon Formatting/Parsing  /////
  // Below are all computed (calculated) fields based on current datetimeFormatLocale (onLocaleChange recalculates them)

  static offsetDateBySeconds(date, numSeconds) {
    return new Date(date.getTime() + numSeconds * 1000);
  }
  /**
   * Returns a new Date in local time zone, interpreting a two digit year as the actual two digit year.
   * e.g. if year = 12, the year set in the new Date object will be exactly 12.
   * Javascript Date constructor has inconsistent behavior with 1 and 2 digit years.
   * Month indexing starts at 0.
   */
  static newLocalDateWithFullYear(year, month, day, hour = 0, minute = 0, second = 0) {
    let date = new Date(year, month, day, hour, minute, second);
    if (0 <= year && year < 100) {
      date.setFullYear(year);
    }
    return date;
  }

  /**
   * Returns a new Date in UTC time zone, interpreting a two digit year as the actual two digit year.
   * e.g. if year = 12, the year set in the new Date object will be exactly 12.
   * Javascript Date constructor has inconsistent behavior with 1 and 2 digit years.
   * Month indexing starts at 0.
   * @param year
   * @param month
   * @param day
   */
  static newUTCDateWithFullYear(year, month, day) {
    let date = new Date(Date.UTC(year, month, day));
    if (0 <= year && year < 100) {
      date.setUTCFullYear(year);
    }
    return date;
  }

  /**
   * Returns a new Date object
   * @param unixTime - Unix time (also known as Epoch time, Posix time, seconds since the Epoch, or UNIX Epoch time)
   */
  static fromUnixTime(unixTime) {
    return new Date(unixTime * 1000);
  }

  /**
   * Given a local Date, return a copy of the date with the HH:MM:SS:mmmm adjusted to be the last possible time in that day.
   */
  static endOfDay(date) {
    const newDate = new Date(date.getTime());
    newDate.setHours(23);
    newDate.setMinutes(59);
    newDate.setSeconds(59);
    newDate.setMilliseconds(999);
    return newDate;
  }

  /**
   * Given an AbCalendarDay, return a new local Date on that day with the HH:MM:SS:mmmm adjusted to be the last possible time in that day.
   */
  static endOfAbCalendarDay(date) {
    return DateUtils.endOfDay(date.localMidnightDate);
  }

  /**
   * Verifies the date object provided exists and is not a "invalid Date" object
   * @param date The date you want to verify
   */
  static isValidDate(date) {
    return ClassUtils.isInstanceOf(date, Date) && !isNaN(date.getTime());
  }
}
_DateUtils = DateUtils;
_defineProperty(DateUtils, "offsetDate", (date, offset) => {
  return _DateUtils.newLocalDateWithFullYear(date.getFullYear() + (offset.year ?? 0), date.getMonth() + (offset.month ?? 0), date.getDate() + (offset.day ?? 0), date.getHours() + (offset.hour ?? 0), date.getMinutes() + (offset.minute ?? 0), date.getSeconds() + (offset.second ?? 0));
});
_defineProperty(DateUtils, "partialOverrideDate", (date, override) => {
  return _DateUtils.newLocalDateWithFullYear(override.year ?? date.getFullYear(), override.month ? override.month - 1 : date.getMonth(), override.day ?? date.getDate(), override.hour ?? date.getHours(), override.minute ?? date.getMinutes(), override.second ?? date.getSeconds());
});
//# sourceMappingURL=DateUtils.js.map
